import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DataService } from '../../../services/data.service';
import { TodoComponent } from '../../../common/widgets/todo/todo.component';

export type LeaveStatus = 'Pending' | 'Under Review' | 'Approved' | 'Rejected';
export type HomeworkStatus = 'Not Complete' | 'Submitted' | 'Graded' | 'Draft' | 'Published' | 'Closed';
export type PassStatus = 'Approved' | 'Pending' | 'Completed' | 'Expired';
export type PaymentStatus = 'Paid' | 'Unpaid';
export type TicketStatus = 'In review' | 'Resolved' | 'Open';
export type PeriodKind = 'lecture' | 'lab' | 'free';
export type ticket_priority = 'Low' | 'Medium' | 'High' | 'Critical';

export interface StudentProfile {
  name: string;
  programme: string;
  roll_no: string;
  avatar: string;
  email: string;
}

export interface HolidayItem {
  datelabel: string;
  title: string;
}

export interface TransportInfo {
  bus_number: string;
  route_name: string;
  pickup_point: string;
  pickup_time: string;
  driver_name: string;
  mobile: string;
  ticket_valid: boolean;
  gap_months: number;
}

export interface UpcomingPayment {
  title: string;
  amount: number;
  due_date: string;
  status: PaymentStatus;
}

export interface LeaveRequest {
  id: number;
  request_type: string;
  from_date: string;
  to_date: string;
  duration: number;
  reason: string;
  status: LeaveStatus;
}

export interface LibraryLedger {
  current_balance: number;
  membership: number;
  book_issue: number;
  late_fees: number;
  other_charges: number;
  total_charges: number;
  total_paid: number;
}

export interface HomeworkItem {
  id: number;
  title: string;
  subject: string;
  due_date: string;
  progress: number;
  status: HomeworkStatus;
}

export interface GatePassItem {
  id: number;
  pass_id: string;
  student_name: string;
  destination: string;
  date: string;
  out_from: string;
  out_to: string;
  status: PassStatus;
  is_active: boolean;
}

export interface PaymentRecord {
  date: string;
  txn_id: string;
  amount: number;
  mode: string;
  status: PaymentStatus;
}

export interface ActivityItem {
  text: string;
  time: string;
  tone: 'danger' | 'primary' | 'success' | 'warning' | 'info';
  icon: string;
}

export interface WeekSlot {
  day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
  start: string;
  end: string;
  subject: string;
  code: string;
  teacher: string;
  room: string;
  kind: PeriodKind;
}

export interface LectureNote {
  title: string;
  type: string;
  icon: string;
}

export interface FeeTicket {
  id: number;
  ticket_no: string;
  title: string;
  date: string;
  assignee: string;
  status: TicketStatus;
}

export interface QuickAction {
  icon: string;
  label: string;
  route: string;
  color: string;
}

@Component({
  selector: 'app-student-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DatePipe, DecimalPipe, TodoComponent],
  templateUrl: './student-dashboard.html',
  styleUrls: ['./dashboard.scss', './student-dashboard.scss'],
})
export class StudentDashboard implements OnInit {
  currentDate = new Date();
  greeting = '';
  toastMsg = signal('');
  loading = false;
  user: any = '';
  monAttRptData: any = '';

  student: StudentProfile = {
    name: 'Rahul Sharma',
    programme: 'B.Sc Computer Science',
    roll_no: '20201231',
    avatar: 'RS',
    email: 'rahul.sharma@edusmart.in',
  };

  attendancePct = 0;
  todayPresent = '';
  todayCourse = '1st Period';
  todayCourseTime = '09:00 AM';
  holidays: HolidayItem[] = [
    // { dateLabel: 'October 14 (Mon)', title: 'Fall Break' },
    // { dateLabel: 'November 11 (Mon)', title: 'Veterans Day' },
    // { dateLabel: 'November 27–29', title: 'Thanksgiving Break' },
  ];
  att_status: any = [];
  transport: TransportInfo = {
    bus_number: '',
    route_name: '',
    pickup_point: '',
    pickup_time: '',
    driver_name: '',
    mobile: '',
    ticket_valid: true,
    gap_months: 0,
  };

  fee_str: any = {};
  markSummary: any = {};

  upcomingPayment = signal<UpcomingPayment>({
    title: 'Transport Fee - Month Year',
    amount: 0,
    due_date: '',
    status: 'Unpaid',
  });

  leaveUsed = signal(14);
  leaveAvailable = signal(20);

  leaveApps = signal<LeaveRequest[]>([
    { id: 1, request_type: 'Sick Leave', from_date: '2026-10-20', to_date: '2026-10-22', duration: 3, reason: 'High Fever', status: 'Pending' },
    { id: 2, request_type: 'Vacation', from_date: '2026-11-15', to_date: '2026-11-18', duration: 4, reason: 'Family Trip', status: 'Under Review' },
    { id: 3, request_type: 'Sick Leave', from_date: '2026-11-15', to_date: '2026-11-20', duration: 3, reason: 'Thanksgiving', status: 'Pending' },
  ]);

  library = signal<LibraryLedger>({
    current_balance: 120,
    membership: 1500,
    book_issue: 200,
    late_fees: 50,
    other_charges: 50,
    total_charges: 1800,
    total_paid: 1680,
  });

  homeworkDate = signal('');

  homework = signal<HomeworkItem[]>([
    // { id: 1, title: 'Advanced Calculus – Problem Set 4', subject: 'Frontier Series', due_date: '2026-10-15', progress: 42, status: 'Not Complete' },
    // { id: 2, title: 'Data Structures – Assignment 3', subject: 'CS-301', due_date: '2026-09-25', progress: 70, status: 'Not Complete' },
    // { id: 3, title: 'Digital Electronics – Lab Report', subject: 'EC-302', due_date: '2026-09-22', progress: 18, status: 'Not Complete' },
    // { id: 4, title: 'DBMS – ER Diagram Submission', subject: 'CS-301', due_date: '2026-09-28', progress: 55, status: 'Not Complete' },
    // { id: 5, title: 'Software Engineering – SRS Draft', subject: 'CS-401', due_date: '2026-10-12', progress: 30, status: 'Not Complete' },
  ]);

  gatePasses = signal<GatePassItem[]>([
    // { id: 1, pass_id: 'GP-123456', student_name: 'Rahul Sharma', destination: 'Library Research', date: '2026-10-25', out_from: '14:00', out_to: '16:00', status: 'Approved', is_active: true },
    // { id: 2, pass_id: 'GP-123401', student_name: 'Rahul Sharma', destination: 'Library Research', date: '2026-10-20', out_from: '14:00', out_to: '16:00', status: 'Approved', is_active: false },
    // { id: 3, pass_id: 'GP-123388', student_name: 'Rahul Sharma', destination: 'Accounts Desk', date: '2026-10-18', out_from: '11:00', out_to: '12:00', status: 'Completed', is_active: false },
  ]);

  payments = signal<PaymentRecord[]>([
    // { date: '2026-09-03', txn_id: 'TRN001456', amount: 1500, mode: 'Online', status: 'Paid' },
    // { date: '2026-08-03', txn_id: 'TRN001387', amount: 1500, mode: 'Online', status: 'Paid' },
    // { date: '2026-07-03', txn_id: 'TRN001254', amount: 1500, mode: 'Bank Transfer', status: 'Paid' },
    // { date: '2026-06-03', txn_id: 'TRN001123', amount: 1500, mode: 'Online', status: 'Paid' },
  ]);

  activities = signal<ActivityItem[]>([
    // { text: 'Fee reminder sent for September 2026.', time: '2 hours ago', tone: 'warning', icon: 'bi-cash-stack' },
    // { text: 'Transport service started for your route.', time: 'Yesterday', tone: 'success', icon: 'bi-bus-front-fill' },
    // { text: 'New book issued: Data Structures.', time: 'Yesterday', tone: 'info', icon: 'bi-journal-bookmark-fill' },
    // { text: 'Assignment was posted for CS-301.', time: '2 days ago', tone: 'primary', icon: 'bi-journal-text' },
    // { text: 'Transport bill generated for August 2026.', time: '4 days ago', tone: 'info', icon: 'bi-receipt' },
    // { text: 'Transport bill is overdue.', time: '5 days ago', tone: 'danger', icon: 'bi-exclamation-triangle-fill' },
  ]);

  readonly weekDays: Array<WeekSlot['day']> = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  weekTimes = signal<any>([
    // { start: '09:00', end: '10:40' },
    // { start: '11:00', end: '12:40' },
    // { start: '13:00', end: '14:40' },
    // { start: '16:00', end: '17:00' },
  ]);

  weeklySchedule = signal<WeekSlot[]>([
    // { day: 'Mon', start: '09:00', end: '10:40', subject: 'Computer Science', code: 'CS-201', teacher: 'EV', room: 'LH-203', kind: 'lecture' },
    // { day: 'Tue', start: '09:00', end: '10:40', subject: 'DBMS', code: 'CS-301', teacher: 'BK', room: 'LH-401', kind: 'lecture' },
    // { day: 'Thu', start: '09:00', end: '10:40', subject: 'Maths', code: 'BS-201', teacher: 'NS', room: 'LH-201', kind: 'lecture' },
    // { day: 'Wed', start: '09:00', end: '10:40', subject: 'Data Structures', code: 'CS-301', teacher: 'KV', room: 'LH-305', kind: 'lecture' },
    // { day: 'Wed', start: '11:00', end: '12:40', subject: 'Digital Electronics', code: 'EC-302', teacher: 'RK', room: 'LH-102', kind: 'lecture' },
    // { day: 'Wed', start: '13:00', end: '14:40', subject: 'LAB: Algorithms', code: 'CS-304', teacher: 'SS', room: 'C-1', kind: 'lab' },
    // { day: 'Thu', start: '11:00', end: '12:40', subject: 'Physics', code: 'BS-202', teacher: 'AS', room: 'LH-102', kind: 'lecture' },
    // { day: 'Fri', start: '11:00', end: '12:40', subject: 'Software Engineering', code: 'CS-401', teacher: 'PN', room: 'LH-210', kind: 'lecture' },
  ]);

  readonly lectureNotes: LectureNote[] = [
    // { title: 'Data Structures – Unit 2', type: 'PDF', icon: 'bi-file-earmark-pdf-fill' },
    // { title: 'Data Structures – Lab 4 Manual', type: 'PDF', icon: 'bi-file-earmark-text-fill' },
    // { title: 'Digital Electronics – Intro PPT', type: 'PPT', icon: 'bi-file-earmark-slides-fill' },
  ];

  readonly quickActions: QuickAction[] = [
    { icon: 'bi-calendar-week-fill', label: 'View Full Schedule', route: '/timetable', color: 'action-primary' },
    { icon: 'bi-arrow-left-right', label: 'Request Class Swap', route: '/timetable', color: 'action-warning' },
    { icon: 'bi-download', label: 'Download Syllabus', route: '/exams', color: 'action-teal' },
  ];

  tickets = signal<FeeTicket[]>([
    // { id: 1, ticket_no: 'TKT-2295', title: 'Late fee charged despite UPI failure', date: '2026-09-21', assignee: 'Accounts – E. Menon', status: 'Resolved' },
    // { id: 2, ticket_no: 'TKT-2318', title: 'Scholarship credit not reflected on invoice', date: '2026-09-27', assignee: 'Accounts – A. Nair', status: 'In review' },
  ]);

  showLeaveModal = signal(false);
  showPassModal = signal(false);
  leaveSubmitted = false;
  passSubmitted = false;
  querySubmitted = false;

  leaveForm = {
    request_type: 'Sick Leave',
    from_date: '',
    to_date: '',
    reason: '',
  };

  passForm = {
    destination: '',
    date: '',
    out_from: '14:00',
    out_to: '16:00',
    reason: '',
  };

  queryForm = {
    concern_type: 'Late fee dispute',
    invoice_ref: 'INV-2026-0148',
    details: '',
    file_name: '',
  };

  readonly concernTypes = [
    'Late fee dispute',
    'Invoice / receipt missing',
    'Scholarship / concession',
    'Transport fee',
    'Library fine',
    'Other',
  ];

  readonly leaveTypes = ['Sick Leave', 'Casual Leave', 'Vacation', 'Medical Leave'];
  readonly qrCells = this.buildQrPattern('GP-123456');

  private nextLeaveId = 4;
  private nextPassId = 4;
  private nextTicketId = 3;
  private passSeq = 123457;

  readonly donutCircumference = 2 * Math.PI * 46;
  attendanceDash = '';     //`${(this.attendancePct / 100) * this.donutCircumference} ${this.donutCircumference}`;

  readonly leaveRemaining = computed(() =>
    Math.max(this.leaveAvailable(), 0),
  );

  readonly pendingLeaveCount = computed(() =>
    this.leaveApps().filter(l => l.status === 'Pending' || l.status === 'Under Review').length,
  );

  readonly activeHomeworkCount = computed(() =>
    this.homework().filter(h => h.status === 'Not Complete').length,
  );

  readonly overdueHomeworkCount = computed(() =>
    this.homework().filter(h => h.status === 'Not Complete' && h.due_date < this.todayIso()).length,
  );

  readonly activePass = computed(() =>
    this.gatePasses().find(p => p.is_active) ?? null,
  );

  readonly passHistory = computed(() =>
    this.gatePasses().filter(p => !p.is_active).slice(0, 3),
  );

  readonly paymentDueInDays = computed(() => {
    const due = new Date(this.upcomingPayment().due_date);
    const now = new Date(this.currentDate);
    now.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return Math.round((due.getTime() - now.getTime()) / 86400000);
  });

  readonly currentSlot = computed(() => this.slotForNow('current'));
  readonly nextSlot = computed(() => this.slotForNow('next'));

  readonly currentProgress = computed(() => {
    const slot = this.currentSlot();
    if (!slot) return 0;
    const now = this.minutesNow();
    const start = this.toMinutes(slot.start);
    const end = this.toMinutes(slot.end);
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.round(((now - start) / (end - start)) * 100);
  });

  constructor(public dataService: DataService) {
    //
  }

  sortArray = (array: any, key: string, isAscending = true) => {
    return [...array].sort((a, b) => {
      const valA = a[key];
      const valB = b[key];

      // Handle strings safely (case-insensitive)
      if (typeof valA === 'string') {
        const comparison = valA.localeCompare(valB);
        return isAscending ? comparison : -comparison;
      }

      // Handle numbers and dates
      return isAscending ? valA - valB : valB - valA;
    });
  };

  async ngOnInit() {
    // const now = new Date();
    // // 12-Hour Format (e.g., "02:30:15 PM")
    // const time12Hour = new Intl.DateTimeFormat('en-US', {
    //   hour: '2-digit',
    //   minute: '2-digit',
    //   second: '2-digit',
    //   hour12: true
    // }).format(now);
    // console.log('Start: '+time12Hour);

    if(!this.dataService.ddData) {
      await this.dataService.getDDData();
    }

    let erp_rem: any = localStorage.getItem("erp_rem");

    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;

      this.student = {
        name: this.user.student.name,
        programme: this.dataService.getClassName(this.user.student.class) + ' Class',
        roll_no: this.user.student.roll_no,
        avatar: this.dataService.getInitName(this.user.student.name),
        email: '',
      };

    }

    const hour = this.currentDate.getHours();
    if (hour < 12) this.greeting = 'Good Morning';
    else if (hour < 17) this.greeting = 'Good Afternoon';
    else this.greeting = 'Good Evening';

    this.loading = true;
    await this.dataService.getSingleRecord('std_monthly_rpt', this.user.id).then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          if(res[i].present==0) {
            res[i].present = res[i].total_days;
          }

          res[i].percentage = (res[i].present * 100) / res[i].total_days;
        }

        this.monAttRptData = res[0];
        this.attendancePct = res[0].percentage.toFixed(2);
        this.attendanceDash = `${(this.attendancePct / 100) * this.donutCircumference} ${this.donutCircumference}`;

        this.markSummary = {
          total:    this.monAttRptData.total_days,
          present:  this.monAttRptData.present,
          absent:   this.monAttRptData.absent,
          late:     this.monAttRptData.late,
          half_day: this.monAttRptData.half_day,
          on_leave: this.monAttRptData.on_leave,
        }
      }
      this.loading = false;
    });

    this.loading = true;
    await this.dataService.getSingleRecord('dash-student', this.user.id).then((res: any) => {
      this.loading = false;
      if(res) {
        this.todayPresent = res.data?.att_status;
        this.todayCourse = res.data?.att_period || '1st Period';
        this.holidays = res.holidays || [];
        this.att_status = res.att_status || [];
        this.transport = {
          bus_number: res.transport?.vehicle_no,
          route_name: res.transport?.route_name,
          pickup_point: res.transport?.stop_name,
          pickup_time: res.transport?.shift,
          driver_name: res.transport?.driver_name,
          mobile: res.transport?.mobile,
          ticket_valid: true,
          gap_months: 0,
        };
        let dt: any = new Date();
        const monthYear = dt.toLocaleString('default', { month: 'short', year: 'numeric' });
        this.upcomingPayment.set({
          title: 'Transport Fee - ' + monthYear,
          amount: res.transport.route_fee,
          due_date: '10-' + (dt.getMonth()+1) + '-' + dt.getFullYear(),
          status: 'Unpaid'
        });

        this.fee_str = res.fee_str;
        let pays: any = [];
        if(res.fee_paid && res.fee_paid.length) {
          for(let i=0; i<res.fee_paid.length; i++) {
            pays.push({
              date: res.fee_paid[i].payment_date, 
              txn_id: res.fee_paid[i].receipt_no, 
              amount: res.fee_paid[i].total_paid, 
              mode: res.fee_paid[i].payment_mode, 
              status: res.fee_paid[i].status
            });
          }

          this.payments.set(pays);
        }

        if(res.hwa && res.hwa.length) {
          for(let i=0; i<res.hwa.length; i++) {
            this.homework.update(list => [
              {
                id: res.hwa[i]?.id, 
                title: res.hwa[i]?.title, 
                subject: this.dataService.getSubjectName(res.hwa[i]?.subject), 
                due_date: res.hwa[i]?.due_date, 
                progress: 0, 
                status: res.hwa[i]?.status
              },
              ...list,
            ]);
          }
        }

        if(res.activity && res.activity.length) {
          for(let i=0; i<res.activity.length; i++) {
            this.activities.update(list => [
              {
                text: res.activity[i].description, 
                time: res.activity[i].cur_date, 
                tone: 'info', 
                icon: 'bi-journal-bookmark-fill'
              },
              ...list,
            ]);
          }
        }

        this.gatePasses.update(list => [
          {
            id: res.gpass?.active?.id,
            pass_id: res.gpass?.active?.pass_no,
            student_name: res.gpass?.active?.student_name,
            destination: this.dataService.getClassName(res.gpass?.active?.class_name) + ' Class',
            date: res.gpass?.active?.reg_date,
            out_from: res.gpass?.active?.out_time,
            out_to: res.gpass?.active?.in_time,
            status: res.gpass?.active?.pass_status,
            is_active: true
          },
          ...list
        ]);
        if(res.gpass && res.gpass.history && res.gpass.history.length) {
          for(let i=0; i<res.gpass.history.length; i++) {
            this.gatePasses.update(list => [
              {
                id: res.gpass?.history[i]?.id,
                pass_id: res.gpass?.history[i]?.pass_no,
                student_name: res.gpass?.history[i]?.student_name,
                destination: this.dataService.getClassName(res.gpass?.history[i]?.class_name) + ' Class',
                date: res.gpass?.history[i]?.reg_date,
                out_from: res.gpass?.history[i]?.out_time,
                out_to: res.gpass?.history[i]?.in_time,
                status: res.gpass?.history[i]?.pass_status,
                is_active: false
              },
              ...list
            ]);
          }
        }

        if(res.time_table && res.time_table.periods && res.time_table.periods.length) {
          for(let i=0; i<res.time_table.periods.length; i++) {
            this.weekTimes.update(list => [
              {
                'start': res.time_table.periods[i].start_time, 
                'end': res.time_table.periods[i].end_time,
                'pname': res.time_table.periods[i].pname,
                'is_break': res.time_table.periods[i].is_break,
                'break_label': res.time_table.periods[i].break_label,
              },
              ...list
            ]);
          }

          // this.sortArray(this.weekTimes(), 'start', false);
        }

        if(res.time_table && res.time_table.assign && res.time_table.assign.length) {
          for(let i=0; i<res.time_table.assign.length; i++) {
            this.weeklySchedule.update(list => [
              {
                day: res.time_table.assign[i].class_day.slice(0, 3), 
                start: res.time_table.assign[i].start_time, 
                end: res.time_table.assign[i].end_time, 
                subject: res.time_table.assign[i].class_subject, 
                code: res.time_table.assign[i].pname, 
                teacher: res.time_table.assign[i].teacher_name, 
                room: res.time_table.assign[i].class_room, 
                kind: 'lecture'
              },
              ...list
            ]);
          }
        }
      }
      this.loading = false;
    });

    // this.tickets
    this.loading = true;
    await this.dataService.getAllRecords('tickets').then((tickets: any) => {
      this.loading = false;
      if(tickets && tickets.length) {
        for(let i=0; i<tickets.length; i++) {
          this.tickets.update(list => [
            {
              id: tickets[i].id, 
              ticket_no: tickets[i].ticket_no, 
              title: tickets[i].title, 
              date: tickets[i].created_at, 
              assignee: tickets[i].assigned_to, 
              status: tickets[i].status
            },
            ...list
          ]);
        }
      }

      this.loading = false;
    });

    // const now2 = new Date();
    // // 12-Hour Format (e.g., "02:30:15 PM")
    // const time12Hour2 = new Intl.DateTimeFormat('en-US', {
    //   hour: '2-digit',
    //   minute: '2-digit',
    //   second: '2-digit',
    //   hour12: true
    // }).format(now2);
    // console.log('Stop: '+time12Hour2);
  }

  getStatusClass(status: LeaveStatus | TicketStatus | PaymentStatus | PassStatus | HomeworkStatus): string {
    const map: Record<string, string> = {
      Approved: 'badge-approved',
      Paid: 'badge-approved',
      Resolved: 'badge-approved',
      Submitted: 'badge-approved',
      Active: 'badge-approved',
      Published: 'badge-approved',
      Graded: 'badge-approved',
      Completed: 'badge-approved',
      Pending: 'badge-pending',
      Draft: 'badge-pending',
      Unpaid: 'badge-pending',
      Open: 'badge-pending',
      'Not Complete': 'badge-pending',
      'Under Review': 'badge-review',
      'In review': 'badge-review',
      Rejected: 'badge-rejected',
      Expired: 'badge-rejected',
    };
    return map[status] ?? 'bg-secondary';
  }

  slotAt(day: WeekSlot['day'], start: string): WeekSlot | null {
    return this.weeklySchedule().find((s: any) => s.day === day && s.start === start) ?? null;
  }

  formatTime(hhmm: string): string {
    const [hStr, m] = hhmm.split(':');
    let h = Number(hStr);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  }

  formatRange(from: string, to: string): string {
    const a = new Date(from);
    const b = new Date(to);
    const sameMonth = a.getMonth() === b.getMonth();
    const start = a.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    const end = b.toLocaleDateString('en-US', sameMonth ? { day: '2-digit' } : { month: 'short', day: '2-digit' });
    return `${start}–${end}`;
  }

  openLeaveModal(): void {
    this.leaveSubmitted = false;
    this.leaveForm = {
      request_type: 'Sick Leave',
      from_date: this.todayIso(),
      to_date: this.todayIso(),
      reason: '',
    };
    this.showLeaveModal.set(true);
  }

  submitLeave(): void {
    this.leaveSubmitted = true;
    if (!this.leaveForm.from_date || !this.leaveForm.to_date || !this.leaveForm.reason.trim()) return;
    const duration = this.daySpan(this.leaveForm.from_date, this.leaveForm.to_date);
    this.leaveApps.update(list => [
      {
        id: this.nextLeaveId++,
        request_type: this.leaveForm.request_type,
        from_date: this.leaveForm.from_date,
        to_date: this.leaveForm.to_date,
        duration,
        reason: this.leaveForm.reason.trim(),
        status: 'Pending',
      },
      ...list,
    ]);
    this.leaveUsed.update(v => v + duration);
    this.leaveAvailable.update(v => Math.max(v - duration, 0));
    this.showLeaveModal.set(false);
    this.showToast('Leave request submitted for review');
  }

  payTransport(): void {
    if (this.upcomingPayment().status === 'Paid') return;
    this.upcomingPayment.update(p => ({ ...p, status: 'Paid' }));
    this.showToast(`₹${this.upcomingPayment().amount.toLocaleString('en-IN')} transport fee paid`);
  }

  payLibrary(): void {
    const due = this.library().current_balance;
    if (due <= 0) return;
    this.library.update(l => ({
      ...l,
      current_balance: 0,
      total_paid: l.total_paid + due,
    }));
    this.showToast(`₹${due} library outstanding paid`);
  }

  submitHomework(item: HomeworkItem): void {
    if (item.status !== 'Not Complete') return;
    this.homework.update(list =>
      list.map(h => h.id === item.id ? { ...h, status: 'Submitted', progress: 100 } : h),
    );
    this.showToast(`Submitted: ${item.title}`);
  }

  openPassModal(): void {
    this.passSubmitted = false;
    this.passForm = {
      destination: '',
      date: this.todayIso(),
      out_from: '14:00',
      out_to: '16:00',
      reason: '',
    };
    this.showPassModal.set(true);
  }

  submitPass(): void {
    this.passSubmitted = true;
    if (!this.passForm.destination.trim() || !this.passForm.date) return;
    this.gatePasses.update(list => [
      {
        id: this.nextPassId++,
        pass_id: `GP-${this.passSeq++}`,
        student_name: this.student.name,
        destination: this.passForm.destination.trim(),
        date: this.passForm.date,
        out_from: this.passForm.out_from,
        out_to: this.passForm.out_to,
        status: 'Pending',
        is_active: false,
      },
      ...list,
    ]);
    this.showPassModal.set(false);
    this.showToast('Gate pass request sent for approval');
  }

  onQueryFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.queryForm.file_name = file ? file.name : '';
  }

  get_sla_hours(p: ticket_priority): number {
    return { Critical: 4, High: 8, Medium: 24, Low: 72 }[p] ?? 24;
  }

  submitQuery(): void {
    const now = new Date().toISOString();
    const sla = this.get_sla_hours('Medium');
    const due = new Date(Date.now() + sla * 3600000).toISOString().substring(0, 16);
    const numbers = '0123456789';
    const customCode = this.dataService.getUUID(5, numbers);

    this.querySubmitted = true;
    if (!this.queryForm.details.trim()) return;
    const num = 2318 + this.nextTicketId;
    this.tickets.update(list => [
      {
        id: this.nextTicketId++,
        ticket_no: `TKT-${new Date().getFullYear()}-`+customCode,
        title: this.queryForm.details.trim().slice(0, 72),
        date: this.todayIso(),
        assignee: 'Accounts desk',
        status: 'Open',
      },
      ...list,
    ]);

    const newTicket = {
      id: this.nextTicketId++,
      ticket_no: `TKT-${new Date().getFullYear()}-`+customCode,
      title: this.queryForm.details.trim().slice(0, 72),
      description:      this.queryForm.details,
      category:         'Other',
      priority:         'Medium',
      status:           'Open',
      requester_name:   this.user.display_name,
      requester_dept:   '',
      requester_email:  '',
      assigned_to:      'Inst',
      due_date:         due,
      resolution_note:  '',
      comments:         '[]',
      created_at:       now, 
      updated_at:       now, 
      resolved_at:      null, 
      sla_hours:        sla,
      erp_key:          this.user.erp_key || ''
    }

    this.doSave('tickets', newTicket);

    this.queryForm.details = '';
    this.queryForm.file_name = '';
    this.querySubmitted = false;
    this.showToast('Fee query submitted to accounts');
  }

  requestSwap(): void {
    this.showToast('Class swap request sent to the academic office');
  }

  downloadNote(note: LectureNote): void {
    this.showToast(`Downloading ${note.title} (${note.type})`);
  }

  downloadSyllabus(): void {
    this.showToast('Syllabus download started');
  }

  private slotForNow(which: 'current' | 'next'): WeekSlot | null {
    if(!this.weekTimes()) return null;

    const day = this.weekdayKey();
    if (!day) return null;
    const now = this.minutesNow();
    const todaySlots = this.weekTimes()
      .map((t: any) => this.slotAt(day, t.start))
      .filter((s: any): s is WeekSlot => !!s)
      .sort((a: any, b: any) => this.toMinutes(a.start) - this.toMinutes(b.start));

    if (which === 'current') {
      return todaySlots.find((s: any) => {
        const start = this.toMinutes(s.start);
        const end = this.toMinutes(s.end);
        return now >= start && now < end;
      }) ?? null;
    }

    return todaySlots.find((s: any) => this.toMinutes(s.start) > now) ?? null;
  }

  private weekdayKey(): WeekSlot['day'] | null {
    const map: Record<number, WeekSlot['day']> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri' };
    return map[this.currentDate.getDay()] ?? null;
  }

  private minutesNow(): number {
    return this.currentDate.getHours() * 60 + this.currentDate.getMinutes();
  }

  private toMinutes(hhmm: string): number {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  }

  todayIso(): string {
    return this.currentDate.toISOString().substring(0, 10);
  }

  private daySpan(from: string, to: string): number {
    const a = new Date(from);
    const b = new Date(to);
    const days = Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
    return Math.max(days, 1);
  }

  private buildQrPattern(seed: string): boolean[] {
    const cells: boolean[] = [];
    let hash = 0;
    for (const ch of seed) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    for (let i = 0; i < 121; i++) {
      hash = (hash * 1664525 + 1013904223) >>> 0;
      const x = i % 11;
      const y = Math.floor(i / 11);
      const finder = (x < 3 && y < 3) || (x > 7 && y < 3) || (x < 3 && y > 7);
      cells.push(finder || hash % 3 !== 0);
    }
    return cells;
  }

  private showToast(msg: string): void {
    this.toastMsg.set(msg);
    setTimeout(() => this.toastMsg.set(''), 2800);
  }



  // ── ACTIONS ───────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        this.ngOnInit();
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        this.ngOnInit();
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
